import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, UrlSegment } from '@angular/router';
import { isSecurityPage, promiseWithCatch } from '../../app.helpers';
import { GuardShared } from '../auth/guard.shared.abstract';

@Injectable()
export class SecondAuthLevelGuard extends GuardShared {
  private exceptionPages: string[] = ['profile/view/:id', 'posts/view/:id'];
  private childPathExceptionPages: string[] = ['posts/all'];

  protected checkGuard(previousPage?: string, route?: ActivatedRouteSnapshot): boolean {
    const isLoggedIn: boolean = this.sharedCheckGuard(previousPage);
    if (isLoggedIn) {
      return this.checkSecondAuthLevelGuard(previousPage, route);
    }
    return isLoggedIn;
  }

  private checkSecondAuthLevelGuard(previousPage?: string, route?: ActivatedRouteSnapshot): boolean {
    if (this.isExceptionPage(previousPage)) {
      return true;
    }

    const fullPath: string = this.getPath(route);
    if (!this.sessionService.is2AuthLvlPassed && this.isSecurityPage(fullPath)) {
      promiseWithCatch(this.router.navigate(['/second-auth-level'], { state: { previousPage } }));
    }

    return this.sessionService.is2AuthLvlPassed;
  }

  private isSecurityPage(currentPath: string): boolean {
    return isSecurityPage(currentPath);
  }

  /**
   * @description Creates full route path for child routing modules.
   * @param { ActivatedRouteSnapshot } route
   * @returns { string }
   */
  private getPath(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map((routeSnapshot: ActivatedRouteSnapshot) =>
        routeSnapshot.url.map((segment: UrlSegment) => segment.toString()).join('/'),
      )
      .filter((urlPart: string) => urlPart.length > 0)
      .join('/');
  }

  /**
   * @description breaks target url into appropriate format and determines
   * whether it is among allowed exceptions
   * @param { string } targetPath
   * @returns { boolean }
   */
  private isExceptionPage(targetPath: string): boolean {
    const splitPath: string[] = targetPath.split('/').filter(Boolean);
    const [routePath, childPath, wildcard] = splitPath;

    if (this.childPathExceptionPages.some((page: string) => page === `${routePath}/${childPath}`)) {
      return true;
    }

    if (!wildcard || wildcard === this.sessionService.userId) {
      return false;
    }

    const formattedPath = `${routePath}/${childPath}/:id`;
    return this.exceptionPages.some((page: string) => page === formattedPath);
  }
}
